import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiBookmark,
  FiBriefcase,
  FiCheck,
  FiClock,
  FiPlus,
  FiSearch,
  FiStar,
} from "react-icons/fi";
import { StarFilled } from "../components/ui/icons";
import { useAuth } from "../context/AuthContext";
import { fetchWithAuth } from "../utils/authFetch";
import {
  JobGridCard,
  JobCardSkeleton,
  JobRowCard,
  JobRowSkeleton,
  JobRows,
  JobLayoutToggle,
  useJobLayout,
} from "./Jobs";
import {
  getUserSettings,
  invalidateUserSettingsCache,
  updateCachedUserSettings,
} from "../lib/userSettingsService";
import Skeleton, { FadeIn, useFillCount } from "../components/Skeleton";
import { COMPANY_TINTS } from "../theme/ThemeContext";
import { jobIdOf, parseDate, firstCompany, initialOf, companyColor } from "../components/JobDetailView";

/* ─────────────────────────────────────────────────────────
   Palette — same navy/copper system as the Jobs page.
───────────────────────────────────────────────────────── */

const Shell = styled.div`
  background: var(--bg);
  color: var(--ink);
  font-family: "Geist", ui-sans-serif, system-ui, -apple-system, "Segoe UI",
    sans-serif;
  font-size: 14px;
  line-height: 1.45;
  margin: -2.5rem;
  min-height: calc(100vh + 5rem);
  /* Bleed past MainContent's padding on every edge, including the strip it
     reserves below 1024px for the fixed menu button, so Top's surface colour
     runs up behind the button instead of leaving a band of page background.
     Top carries the button's clearance as padding-top. min-height tracks the
     total vertical bleed (top + bottom) so the background still fills. */
  @media (max-width: 1024px) {
    margin: -4.75rem -1.5rem -1.5rem;
    min-height: calc(100vh + 6.25rem);
  }
  @media (max-width: 768px) {
    margin: -4.25rem -1rem -1rem;
    min-height: calc(100vh + 5.25rem);
  }
  @media (max-width: 450px) {
    margin: -4rem -0.75rem -0.75rem;
    min-height: calc(100vh + 4.75rem);
  }
  @media (max-width: 390px) {
    margin: -3.8rem -0.5rem -0.5rem;
    min-height: calc(100vh + 4.3rem);
  }
`;

/* ─────────────────────────────────────────────────────────
   Header / tabs
───────────────────────────────────────────────────────── */
const Top = styled.div`
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  padding: 18px 36px 14px;
  /* Shorthand first in each block, then padding-top, or the shorthand wins. */
  @media (max-width: 1024px) {
    padding-top: calc(18px + 4.75rem);
  }
  @media (max-width: 768px) {
    padding: 14px 18px 12px;
    padding-top: calc(14px + 4.25rem);
  }
  @media (max-width: 450px) {
    padding-top: calc(14px + 4rem);
  }
  @media (max-width: 390px) {
    padding-top: calc(14px + 3.8rem);
  }
`;
const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`;
const PageTitle = styled.h1`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 38px;
  line-height: 1;
  color: var(--ink);
  letter-spacing: -0.01em;
  margin: 0;
  font-weight: 400;
  @media (max-width: 768px) {
    font-size: 30px;
  }
`;
const TitleMeta = styled.span`
  font-family: "Geist", sans-serif;
  font-size: 13.5px;
  color: var(--ink-muted);
  margin-left: 16px;
  font-weight: 400;
  letter-spacing: 0;
  b {
    color: var(--ink);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .accent {
    color: var(--accent);
  }
  @media (max-width: 768px) {
    display: block;
    margin-left: 0;
    margin-top: 4px;
  }
`;
const Tabs = styled.div`
  display: inline-flex;
  background: var(--bg-2);
  border-radius: 999px;
  padding: 3px;
  margin-top: 14px;
  max-width: 100%;
`;
const Tab = styled.button`
  height: 30px;
  padding: 0 16px;
  border: 0;
  background: transparent;
  color: var(--ink-soft);
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  &:hover {
    color: var(--ink);
  }
  /* Selected view: a neutral raised pill; colour is kept for actions (blue) and plan limits (orange). */
  ${({ $active }) =>
    $active &&
    css`
      background: var(--surface);
      color: var(--ink);
      box-shadow: 0 1px 3px color-mix(in srgb, #000 14%, transparent), 0 0 0 1px var(--line);
      &:hover {
        color: var(--ink);
      }
    `}
  /* Global 44px touch rule would fatten the pill bar; keep it a compact pill. */
  @media (max-width: 450px) {
    min-height: 0;
    height: 34px;
    padding: 0 12px;
  }
`;
const TabCount = styled.span`
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s,
    transform 0.06s;
  white-space: nowrap;
  &:hover {
    background: var(--surface-tint);
    border-color: var(--line-strong);
  }
  &:active {
    transform: translateY(0.5px);
  }
  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  ${({ $primary }) =>
    $primary &&
    css`
      background: var(--accent);
      color: var(--on-fill);
      border-color: var(--accent);
      &:hover {
        background: var(--accent-hover);
        border-color: var(--accent-hover);
      }
    `}
  ${({ $ghost }) =>
    $ghost &&
    css`
      background: transparent;
      border-color: transparent;
      &:hover {
        background: var(--bg-2);
      }
    `}
  ${({ $sm }) =>
    $sm &&
    css`
      height: 28px;
      padding: 0 10px;
      font-size: 12.5px;
      border-radius: 7px;
    `}
`;

/* ─────────────────────────────────────────────────────────
   Toolbar (search + counter)
───────────────────────────────────────────────────────── */
const BmToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;
const Search = styled.div`
  flex: 1;
  max-width: 360px;
  position: relative;
  display: flex;
  align-items: center;
  /* flex-basis 0 lets the search squeeze to a sliver beside the seg tabs; give it its own line. */
  @media (max-width: 450px) {
    flex: 1 1 100%;
    max-width: none;
  }
  svg.lead {
    position: absolute;
    left: 11px;
    color: var(--ink-muted);
    pointer-events: none;
  }
  input {
    width: 100%;
    height: 34px;
    padding: 0 12px 0 36px;
    border-radius: 8px;
    border: 1px solid var(--line);
    background: var(--surface-tint);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;
    outline: 0;
  }
  input::placeholder {
    color: var(--ink-muted);
  }
  input:focus {
    background: var(--surface);
    border-color: var(--ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
`;
const BmCount = styled.div`
  font-size: 13px;
  color: var(--ink-muted);
  font-variant-numeric: tabular-nums;
  b {
    color: var(--ink);
    font-weight: 600;
  }
`;
// Open/closed filter inside the saved-jobs pane. Deliberately a lighter
// segmented control — the dark pill Tabs above it switch jobs vs companies.
const SegTabs = styled.div`
  display: inline-flex;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface-tint);
  padding: 2px;
  gap: 2px;
`;
const SegTab = styled.button`
  height: 28px;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: var(--ink-soft);
  border-radius: 6px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s;
  &:hover {
    color: var(--ink);
  }
  ${({ $active }) =>
    $active &&
    css`
      background: var(--surface);
      color: var(--ink);
      font-weight: 600;
      box-shadow: var(--shadow-sm);
    `}
  @media (max-width: 450px) {
    min-height: 0;
    height: 32px;
  }
`;
const SegCount = styled.span`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  opacity: 0.65;
`;
// Empty state for a filter tab that matched nothing (the pane itself has jobs).
const SJEmptyTab = styled.div`
  padding: 44px 20px;
  text-align: center;
  color: var(--ink-muted);
  font-size: 13.5px;
  border: 1px dashed var(--line);
  border-radius: 14px;
  b {
    display: block;
    color: var(--ink);
    font-size: 14.5px;
    font-weight: 600;
    margin-bottom: 5px;
  }
`;

/* ─────────────────────────────────────────────────────────
   Logos / chips / tags
───────────────────────────────────────────────────────── */
const CompanyLogo = styled.span`
  display: inline-grid;
  place-items: center;
  width: ${({ $size }) => $size || 44}px;
  height: ${({ $size }) => $size || 44}px;
  border-radius: 11px;
  background: ${({ $bg }) => $bg || COMPANY_TINTS[0]};
  color: var(--on-fill);
  font-weight: 700;
  font-size: ${({ $size }) => Math.round((($size || 44) * 0.42) * 10) / 10}px;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
`;
const Pane = styled.div`
  padding: 24px 32px 60px;
  max-width: 1280px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 16px 18px 40px;
  }
`;

/* ─────────────────────────────────────────────────────────
   Saved Job card
───────────────────────────────────────────────────────── */
const SJGrid = styled.div`
  display: grid;
  /* min() so a 340px track can't overflow a 360–375px phone. */
  grid-template-columns: repeat(auto-fill, minmax(min(340px, 100%), 1fr));
  gap: 14px;
`;
const SJStar = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--accent-soft);
  border: 0;
  color: var(--star);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.12s;
  &:hover {
    background: var(--accent-soft);
    filter: brightness(0.95);
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const SJSep = styled.span`
  color: var(--ink-faint);
  margin: 0 5px;
`;

/* ─────────────────────────────────────────────────────────
   Saved company card
───────────────────────────────────────────────────────── */
const SCGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: 12px;
`;
const SCCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  transition: border-color 0.12s, box-shadow 0.12s, transform 0.06s;
  /* The whole card opens the company page; inner controls stop the click. */
  &[role="link"] {
    cursor: pointer;
  }
  &:hover {
    border-color: var(--line-strong);
    box-shadow: var(--shadow-md);
  }
  &:focus-visible {
    outline: 2px solid var(--accent, currentColor);
    outline-offset: 2px;
  }
`;
const SCHead = styled.div`
  display: flex;
  gap: 13px;
  align-items: center;
`;
const SCName = styled.div`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 21px;
  line-height: 1.15;
  color: var(--ink);
  letter-spacing: -0.005em;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SCMeta = styled.div`
  font-size: 12px;
  color: var(--ink-muted);
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SCFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--line-soft);
`;
/* Rolling posting counts under the header. */
const SCStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 13px;
  font-size: 12px;
  color: var(--ink-muted);
  .stat b {
    color: var(--ink);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .stat.hot b {
    color: var(--success);
  }
  .sep {
    color: var(--ink-faint);
  }
`;
/* Latest 5 postings from the company. */
const SCJobs = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 12px 0 0;
  border-top: 1px solid var(--line-soft);
  display: flex;
  flex-direction: column;
  gap: 1px;
`;
const SCJob = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  margin: 0 -8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s;
  &:hover {
    background: var(--surface-tint);
  }
  .t {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &:hover .t {
    color: var(--accent);
  }
  .when {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--ink-muted);
    font-variant-numeric: tabular-nums;
  }
`;
const SCJobsEmpty = styled.div`
  margin-top: 12px;
  padding: 14px 0 2px;
  border-top: 1px solid var(--line-soft);
  font-size: 12.5px;
  color: var(--ink-muted);
  text-align: center;
`;
const SCMore = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--accent);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  &:hover {
    text-decoration: underline;
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;


/* ─────────────────────────────────────────────────────────
   Empty state
───────────────────────────────────────────────────────── */
const Empty = styled.div`
  display: grid;
  place-items: center;
  text-align: center;
  padding: 80px 20px;
  min-height: 60vh;
  /* Accent-tinted tile — matches the Dashboard empty state. */
  .art {
    width: 96px;
    height: 96px;
    border-radius: 24px;
    background: linear-gradient(155deg, var(--accent-soft), var(--surface-tint));
    border: 1px solid var(--line);
    color: var(--accent);
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    box-shadow: var(--shadow-md);
    box-shadow: 0 8px 22px -10px color-mix(in srgb, var(--accent) 35%, transparent);
  }
  h3 {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 26px;
    margin: 0 0 6px;
    color: var(--ink);
    font-weight: 400;
  }
  p {
    color: var(--ink-muted);
    max-width: 380px;
    margin: 0 auto 14px;
    font-size: 13.5px;
    line-height: 1.5;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 2500;
  background: var(--ink);
  color: var(--bg);
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: var(--shadow-lg);
  display: flex;
  gap: 8px;
  align-items: center;
  @media (max-width: 450px) {
    left: 16px;
    right: 16px;
    bottom: 16px;
  }
`;

/* ─────────────────────────────────────────────────────────
   Per-view loading skeletons — shaped like the real cards so
   layout space is reserved (no pop-in / height jumps).
───────────────────────────────────────────────────────── */
function SavedJobsSkeleton() {
  // Skeleton follows the saved layout preference so the placeholder
  // matches whatever shape the real jobs will land in; enough of them to fill the screen.
  const [layout] = useJobLayout();
  const List = layout === "rows" ? JobRows : SJGrid;
  const Item = layout === "rows" ? JobRowSkeleton : JobCardSkeleton;
  const ref = useRef(null);
  const count = useFillCount(ref, { min: 6 });
  return (
    <Pane>
      <BmToolbar>
        <Search>
          <FiSearch className="lead" size={14} />
          <input disabled placeholder="Search saved jobs…" />
        </Search>
        <div style={{ flex: 1 }} />
        <Skeleton width={90} height={14} />
      </BmToolbar>
      <List ref={ref}>
        {Array.from({ length: count }).map((_, i) => (
          <Item key={i} />
        ))}
      </List>
    </Pane>
  );
}

function SavedCompaniesSkeleton() {
  const ref = useRef(null);
  const count = useFillCount(ref, { min: 6 });
  return (
    <Pane>
      <BmToolbar>
        <Search>
          <FiSearch className="lead" size={14} />
          <input disabled placeholder="Search saved companies…" />
        </Search>
        <div style={{ flex: 1 }} />
        <Skeleton width={100} height={14} />
      </BmToolbar>
      <SCGrid ref={ref}>
        {Array.from({ length: count }).map((_, i) => (
          <SCCard key={i}>
            <SCHead>
              <Skeleton width={44} height={44} radius="11px" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Skeleton width="70%" height={20} mb={6} />
                <Skeleton width="50%" height={12} />
              </div>
              <Skeleton width={32} height={32} radius="8px" />
            </SCHead>
            <div style={{ marginTop: 13 }}>
              <Skeleton width={150} height={13} />
            </div>
            <SCJobs as="div">
              {Array.from({ length: 3 }).map((_, k) => (
                <div key={k} style={{ padding: "6px 0" }}>
                  <Skeleton width={`${80 - k * 12}%`} height={13} />
                </div>
              ))}
            </SCJobs>
          </SCCard>
        ))}
      </SCGrid>
    </Pane>
  );
}


/* ─────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */
// Finer-grained relative time for individual job postings (hours matter here).
function jobAgo(value) {
  const d = parseDate(value);
  if (!d || isNaN(d)) return "";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* ─────────────────────────────────────────────────────────
   Sub-views
───────────────────────────────────────────────────────── */
const SAVED_TABS = [
  { key: "all", label: "All jobs" },
  { key: "open", label: "Open jobs" },
  { key: "closed", label: "Closed jobs" },
];

function SavedJobsView({ jobs, bookmarks, watchlistMeta, onUnsave, onOpen }) {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");
  const [layout, setLayout] = useJobLayout();

  // `closed` is stamped by the server when a bookmark resolved from the
  // archive instead of the live corpus — i.e. the posting is no longer up.
  const counts = useMemo(() => {
    const closed = jobs.filter((j) => j.closed).length;
    return { all: jobs.length, open: jobs.length - closed, closed };
  }, [jobs]);

  const filtered = useMemo(() => {
    let list = jobs;
    if (tab === "open") list = list.filter((j) => !j.closed);
    else if (tab === "closed") list = list.filter((j) => j.closed);
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((j) => {
      const title = (j.title || j.job_title || "").toLowerCase();
      const company = firstCompany(j.source).toLowerCase();
      return title.includes(q) || company.includes(q);
    });
  }, [jobs, query, tab]);

  // Keyed off resolved jobs, not raw bookmark ids — closed postings still
  // resolve (from the archive), but a bookmark we can't hydrate at all should
  // read as "no saved jobs", not an empty grid.
  if (jobs.length === 0) {
    return (
      <Empty>
        <div>
          <div className="art">
            <FiBookmark size={48} strokeWidth={1.3} />
          </div>
          <h3>No saved jobs yet</h3>
          <p>
            Star a job from your job board to pin it here. We'll keep it
            handy for sourcing later.
          </p>
          <Btn $primary as="a" href="/jobs">
            <FiBriefcase size={14} /> Browse jobs
          </Btn>
        </div>
      </Empty>
    );
  }

  return (
    <Pane>
      <BmToolbar>
        <SegTabs role="tablist">
          {SAVED_TABS.map((t) => (
            <SegTab
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              $active={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <SegCount>{counts[t.key]}</SegCount>
            </SegTab>
          ))}
        </SegTabs>
        <Search>
          <FiSearch className="lead" size={14} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search saved jobs…"
          />
        </Search>
        <div style={{ flex: 1 }} />
        <BmCount>
          <b>{filtered.length}</b> of {counts[tab]}{" "}
          {counts[tab] === 1 ? "job" : "jobs"}
        </BmCount>
        <JobLayoutToggle value={layout} onChange={setLayout} />
      </BmToolbar>
      {filtered.length === 0 ? (
        <SJEmptyTab>
          <b>
            {query
              ? "No matches"
              : tab === "closed"
                ? "No closed jobs"
                : "No open jobs"}
          </b>
          {query
            ? "Try a different title or company."
            : tab === "closed"
              ? "None of your saved jobs have closed yet."
              : "Every job you've saved has since closed."}
        </SJEmptyTab>
      ) : (
        (() => {
          const List = layout === "rows" ? JobRows : SJGrid;
          const Item = layout === "rows" ? JobRowCard : JobGridCard;
          return (
            <List>
              {filtered.map((j) => (
                <Item
                  key={jobIdOf(j)}
                  job={j}
                  saved
                  onOpen={onOpen}
                  onViewDetails={onOpen}
                  onToggleSave={onUnsave}
                />
              ))}
            </List>
          );
        })()
      )}
    </Pane>
  );
}

function SavedCompaniesView({
  companies,
  onUnsave,
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!query) return companies;
    const q = query.toLowerCase();
    return companies.filter((c) => c.name.toLowerCase().includes(q));
  }, [companies, query]);

  // Latest postings + rolling counts for every saved company, in one request.
  const [activity, setActivity] = useState(null); // null = loading, {} = loaded
  const companiesKey = useMemo(
    () => companies.map((c) => c.name).join("|"),
    [companies],
  );
  useEffect(() => {
    if (!companiesKey) {
      setActivity({});
      return undefined;
    }
    let alive = true;
    setActivity(null);
    fetchWithAuth(
      `/api/companies/job-activity?sources=${encodeURIComponent(companiesKey)}`,
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => alive && setActivity(data?.activity || {}))
      .catch(() => alive && setActivity({}));
    return () => {
      alive = false;
    };
  }, [companiesKey]);

  const openCompanyJobs = useCallback(
    (name) => navigate(`/jobs?source=${encodeURIComponent(name)}`),
    [navigate],
  );
  const openJobDetail = useCallback(
    (job) => {
      const id = job?._id || jobIdOf(job);
      if (!id) return;
      navigate(`/jobs?jobId=${encodeURIComponent(id)}`, {
        state: { fromExternal: true, fromLabel: "saved" },
      });
    },
    [navigate],
  );

  if (companies.length === 0) {
    return (
      <Empty>
        <div>
          <div className="art">
            <FiStar size={48} strokeWidth={1.3} />
          </div>
          <h3>No saved companies yet</h3>
          <p>
            Click <b>Save</b> on a firm from Company research to pin it here.
            Great for shortlisting target firms before outreach.
          </p>
          <Btn $primary as="a" href="/research">
            <FiSearch size={14} /> Browse companies
          </Btn>
        </div>
      </Empty>
    );
  }

  return (
    <Pane>
      <BmToolbar>
        <Search>
          <FiSearch className="lead" size={14} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search saved companies…"
          />
        </Search>
        <div style={{ flex: 1 }} />
        <BmCount>
          <b>{filtered.length}</b> of {companies.length}{" "}
          {companies.length === 1 ? "company" : "companies"}
        </BmCount>
      </BmToolbar>
      <SCGrid>
        {filtered.map((co) => {
          const act = activity?.[co.name];
          const loading = activity === null;
          const jobs = act?.latest || [];
          return (
            <SCCard
              key={co.name}
              role="link"
              tabIndex={0}
              title={`Open ${co.name}`}
              onClick={() => navigate(`/research/${encodeURIComponent(co.name)}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/research/${encodeURIComponent(co.name)}`);
                }
              }}
            >
              <SCHead>
                <CompanyLogo $size={44} $bg={companyColor(co.name)}>
                  {initialOf(co.name)}
                </CompanyLogo>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SCName>{co.name}</SCName>
                  {(co.industry || co.location) && (
                    <SCMeta>
                      {co.industry || "—"}
                      {co.location && (
                        <>
                          <SJSep>·</SJSep>
                          {co.location}
                        </>
                      )}
                    </SCMeta>
                  )}
                </div>
                <SJStar
                  title="Remove from saved"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnsave(co.name);
                  }}
                >
                  <StarFilled size={14} />
                </SJStar>
              </SCHead>

              <SCStats>
                {loading ? (
                  <Skeleton width={150} height={13} />
                ) : (
                  <>
                    <span className={`stat${act?.count24h ? " hot" : ""}`}>
                      <b>
                        {act?.count24h ? `+${act.count24h}` : 0}
                      </b>{" "}
                      in 24h
                    </span>
                    <span className="sep">·</span>
                    <span className="stat">
                      <b>{act?.countWeek || 0}</b> this week
                    </span>
                  </>
                )}
              </SCStats>

              {loading ? (
                <SCJobs as="div">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} style={{ padding: "6px 0" }}>
                      <Skeleton width={`${80 - i * 12}%`} height={13} />
                    </div>
                  ))}
                </SCJobs>
              ) : jobs.length > 0 ? (
                <SCJobs>
                  {jobs.map((j) => (
                    <SCJob
                      key={j._id}
                      title={j.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        openJobDetail(j);
                      }}
                    >
                      <span className="t">{j.title}</span>
                      <span className="when">
                        <FiClock size={12} /> {jobAgo(j.posted_date) || "—"}
                      </span>
                    </SCJob>
                  ))}
                </SCJobs>
              ) : (
                <SCJobsEmpty>No recent postings from this company.</SCJobsEmpty>
              )}

              {act && act.total > jobs.length ? (
                <SCFoot>
                  <SCMore
                    onClick={(e) => {
                      e.stopPropagation();
                      openCompanyJobs(co.name);
                    }}
                  >
                    All {act.total} jobs →
                  </SCMore>
                </SCFoot>
              ) : null}
            </SCCard>
          );
        })}
      </SCGrid>
    </Pane>
  );
}


/* ─────────────────────────────────────────────────────────
   Main page
───────────────────────────────────────────────────────── */
function Watchlist() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = user?.sub ?? null;

  // URL ?view= → internal tab key
  const rawView = searchParams.get("view");
  const view = rawView === "companies" ? "companies" : "saved"; // "manual"/missing → saved

  const switchView = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      params.set("view", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }, []);

  // User settings
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [watchlistMeta, setWatchlistMeta] = useState({});
  const [savedCompanies, setSavedCompanies] = useState([]); // [{name, savedAt}]
  const [autoWatchFilters, setAutoWatchFilters] = useState([]);

  // Fetched jobs for the saved-jobs view
  const [savedJobs, setSavedJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  // Load user settings
  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getUserSettings(userId);
        if (cancelled || !data) return;
        const watchlistItems = data.watchlist || [];
        const ids = watchlistItems.map((item) => {
          const raw = item.job_id;
          if (raw && typeof raw === "object" && raw.$oid) return String(raw.$oid);
          return String(raw);
        });
        setBookmarks(ids);
        const meta = {};
        for (const item of watchlistItems) {
          const raw = item.job_id;
          const key =
            raw && typeof raw === "object" && raw.$oid
              ? String(raw.$oid)
              : String(raw);
          meta[key] = item.added_date || null;
        }
        setWatchlistMeta(meta);
        const cos = Array.isArray(data.companies)
          ? data.companies.map((c) =>
              typeof c === "string"
                ? { name: c, savedAt: null }
                : { name: String(c?.name || ""), savedAt: c?.savedAt || null },
            ).filter((c) => c.name)
          : [];
        setSavedCompanies(cos);
        setAutoWatchFilters(data.autoWatchFilters || []);
      } catch (err) {
        console.error("Failed to load user settings:", err);
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId]);

  // Fetch saved jobs (by IDs) whenever bookmarks change
  useEffect(() => {
    if (bookmarks.length === 0) {
      setSavedJobs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setJobsLoading(true);
      try {
        // /api/jobs/by-ids accepts max 100 IDs — chunk if larger.
        const chunks = [];
        for (let i = 0; i < bookmarks.length; i += 100) {
          chunks.push(bookmarks.slice(i, i + 100));
        }
        const results = await Promise.all(
          chunks.map((ids) =>
            // includeClosed: a saved job that has since closed is gone from the
            // live corpus — resolve it from the archive so it stays on the board.
            fetchWithAuth("/api/jobs/by-ids", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ids, includeClosed: true }),
            })
              .then((r) => (r.ok ? r.json() : []))
              .catch(() => []),
          ),
        );
        if (cancelled) return;
        const merged = [].concat(...results);
        // Preserve bookmark order (most recently bookmarked first).
        const byId = new Map();
        for (const j of merged) byId.set(jobIdOf(j), j);
        const ordered = bookmarks
          .map((id) => byId.get(String(id)))
          .filter(Boolean);
        setSavedJobs(ordered);
      } catch (err) {
        if (!cancelled) setSavedJobs([]);
      } finally {
        if (!cancelled) setJobsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookmarks]);

  // Persist user-settings change
  const persistSettings = useCallback(
    async (patch) => {
      if (!isAuthenticated || !userId) return;
      try {
        // Update the shared cache before the POST so other pages see this immediately.
        updateCachedUserSettings(userId, patch);
        await fetchWithAuth(
          `/api/user-settings/${encodeURIComponent(userId)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
          },
        );
        invalidateUserSettingsCache(userId);
      } catch (err) {
        console.error("Failed to persist user settings:", err);
      }
    },
    [isAuthenticated, userId],
  );

  /* ── Saved job actions ─────────────────────────────── */
  const unsaveJob = useCallback(
    (job) => {
      const id = jobIdOf(job);
      const nextIds = bookmarks.filter((b) => String(b) !== id);
      setBookmarks(nextIds);
      const watchlist = nextIds.map((jobId) => ({
        job_id: jobId,
        added_date: watchlistMeta[jobId] || null,
      }));
      persistSettings({ watchlist, autoWatchFilters });
      showToast("Removed from saved");
    },
    [bookmarks, watchlistMeta, autoWatchFilters, persistSettings, showToast],
  );
  const openJob = useCallback(
    (job) => {
      const id = jobIdOf(job);
      if (!id) return;
      navigate(`/jobs?jobId=${encodeURIComponent(id)}`, {
        state: { fromExternal: true, fromLabel: "saved" },
      });
    },
    [navigate],
  );

  /* ── Saved company actions ─────────────────────────── */
  const unsaveCompany = useCallback(
    (name) => {
      const next = savedCompanies.filter((c) => c.name !== name);
      setSavedCompanies(next);
      persistSettings({ companies: next.map((c) => c.name) });
      showToast("Removed from saved companies");
    },
    [savedCompanies, persistSettings, showToast],
  );

  // Initial settings load is in-flight (no flag yet, derive from auth + done bit).
  const settingsLoading = isAuthenticated && !!userId && !settingsLoaded;

  // Saved-jobs count for the header/tab: every bookmark that resolves, closed
  // postings included — only unhydratable bookmarks drop out of the count.
  // null while loading → render a skeleton instead of a partial number.
  const savedJobsCount =
    settingsLoaded && !jobsLoading ? savedJobs.length : null;

  /* ── Toolbar primary action label by tab ───────────── */
  const primaryAction = (() => {
    if (view === "companies")
      return {
        label: "Save a company",
        onClick: () => navigate("/research"),
      };
    return { label: "Browse jobs", onClick: () => navigate("/jobs") };
  })();

  return (
    <Shell>
      <Top>
        <TopRow>
          <div style={{ minWidth: 0 }}>
            <PageTitle>
              Bookmarks
              <TitleMeta>
                {savedJobsCount == null ? (
                  <Skeleton
                    width={16}
                    height={10}
                    radius="4px"
                    inline
                    style={{ verticalAlign: "middle" }}
                  />
                ) : (
                  <b>{savedJobsCount}</b>
                )}{" "}
                saved {savedJobsCount === 1 ? "job" : "jobs"} ·{" "}
                <b>{savedCompanies.length}</b>{" "}
                {savedCompanies.length === 1 ? "company" : "companies"}              </TitleMeta>
            </PageTitle>
            <Tabs data-tour="saved-tabs">
              <Tab $active={view === "saved"} onClick={() => switchView("saved")}>
                <FiBookmark size={12} /> Saved jobs{" "}
                <TabCount>
                  {savedJobsCount == null ? (
                    <Skeleton
                      width={12}
                      height={9}
                      radius="4px"
                      inline
                      style={{ verticalAlign: "middle" }}
                    />
                  ) : (
                    savedJobsCount
                  )}
                </TabCount>
              </Tab>
              <Tab
                $active={view === "companies"}
                onClick={() => switchView("companies")}
              >
                <FiStar size={12} /> Saved companies{" "}
                <TabCount>{savedCompanies.length}</TabCount>
              </Tab>
            </Tabs>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Btn $primary onClick={primaryAction.onClick}>
              <FiPlus size={14} /> {primaryAction.label}
            </Btn>
          </div>
        </TopRow>
      </Top>

      {view === "saved" &&
        (settingsLoading || (bookmarks.length > 0 && jobsLoading) ? (
          <SavedJobsSkeleton />
        ) : (
          <FadeIn data-tour="saved-list">
            <SavedJobsView
              jobs={savedJobs}
              bookmarks={bookmarks}
              watchlistMeta={watchlistMeta}
              onUnsave={unsaveJob}
              onOpen={openJob}
            />
          </FadeIn>
        ))}
      {view === "companies" &&
        (settingsLoading ? (
          <SavedCompaniesSkeleton />
        ) : (
          <FadeIn>
            <SavedCompaniesView
              companies={savedCompanies}
              onUnsave={unsaveCompany}
            />
          </FadeIn>
        ))}

      {toast &&
        createPortal(
          <Toast>
            <FiCheck size={14} strokeWidth={3} style={{ color: "var(--success)" }} />
            {toast}
          </Toast>,
          document.body,
        )}
    </Shell>
  );
}

export default Watchlist;
